import React, { useState, useRef, useEffect } from 'react';
import './FilterBar.css';

export function MultiSelect({ label, options, selected, onChange, icon, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const dropRef = useRef(null);
  const isMobile = () => window.innerWidth <= 640;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target) &&
          dropRef.current && !dropRef.current.contains(e.target)) {
        setOpen(false); setSearch('');
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') { setOpen(false); setSearch(''); } }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const allSelected = selected.length === 0;

  function toggle(val) {
    if (val === '__ALL__') { onChange([]); return; }
    const next = selected.includes(val)
      ? selected.filter(v => v !== val)
      : [...selected, val];
    onChange(next);
  }

  const filtered = search.trim()
    ? options.filter(o => {
        const v = typeof o === 'object' ? o.value : o;
        return (v || '').toLowerCase().includes(search.toLowerCase());
      })
    : options;

  // Normalize: options can be strings or {value, count}
  const normalize = (o) => typeof o === 'object' ? o : { value: o };
  const allOpts = options.map(normalize);
  const filteredOpts = filtered.map(normalize);
  const totalCount = allOpts.reduce((s, o) => s + (o.count || 0), 0);

  const displayLabel = allSelected
    ? label
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  function toggleOpen() {
    if (disabled) return;
    setOpen(o => !o);
    setSearch('');
  }

  // Position dropdown — on mobile use fixed bottom sheet style (via CSS)
  // On desktop position relative to trigger
  const dropStyle = {};
  if (!isMobile() && open && wrapRef.current) {
    const rect = wrapRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 280) {
      dropStyle.top = 'auto';
      dropStyle.bottom = 'calc(100% + 5px)';
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      {open && isMobile() && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9998,
            background: 'rgba(0,0,0,0.3)',
            backdropFilter: 'blur(2px)',
          }}
          onClick={() => { setOpen(false); setSearch(''); }}
        />
      )}
      <div
        className={`ms-wrap ${open ? 'ms-open' : ''} ${disabled ? 'ms-disabled' : ''}`}
        ref={wrapRef}
        style={{ position: 'relative' }}
      >
        <button
          className={`ms-trigger ${!allSelected ? 'ms-has-value' : ''}`}
          onClick={toggleOpen}
          disabled={disabled}
          type="button"
        >
          {icon && <span className="ms-icon">{icon}</span>}
          <span className="ms-label">{displayLabel}</span>
          {!allSelected && <span className="ms-badge">{selected.length}</span>}
          <svg className="ms-arrow" width="10" height="6" viewBox="0 0 10 6">
            <path d={open ? 'M1 5L5 1L9 5' : 'M1 1L5 5L9 1'} stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
          </svg>
        </button>

        {open && (
          <div className="ms-dropdown" ref={dropRef} style={dropStyle}>
            <div className="ms-header">
              <span className="ms-title">{label}</span>
              {!allSelected && (
                <button className="ms-clear-btn" type="button" onClick={() => onChange([])}>✕ Clear</button>
              )}
            </div>

            {allOpts.length > 6 && (
              <div className="ms-search-wrap">
                <input
                  className="ms-search"
                  placeholder="Search…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
            )}

            <div className="ms-list">
              {!search && (
                <label className={`ms-item ms-all-item ${allSelected ? 'ms-checked' : ''}`}>
                  <input type="checkbox" checked={allSelected} onChange={() => onChange([])} />
                  <span className="ms-check-box" />
                  <span className="ms-item-label">All {label}</span>
                  {totalCount > 0 && <span className="ms-item-count">{totalCount}</span>}
                </label>
              )}
              {filteredOpts.map(opt => (
                <label
                  key={opt.value}
                  className={`ms-item ${selected.includes(opt.value) ? 'ms-checked' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                  />
                  <span className="ms-check-box" />
                  <span className="ms-item-label">{opt.value || '(blank)'}</span>
                  {opt.count !== undefined && (
                    <span className="ms-item-count">{opt.count}</span>
                  )}
                </label>
              ))}
              {filteredOpts.length === 0 && (
                <div className="ms-no-results">No results for "{search}"</div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default MultiSelect;
